import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { LogIn, Timer, CalendarPlus, BadgeCheck, Gift, Loader2, LockOpen, Lock, DatabaseBackup } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const fmt = (d) => (d ? new Date(d.length === 10 ? `${d}T12:00:00` : d).toLocaleDateString("pt-BR") : "");

export const STATUS = {
  teste: ["Teste", "bg-sky-950/40 text-sky-300 border-sky-800"],
  teste_vencido: ["Teste vencido", "bg-red-950/40 text-red-300 border-red-900"],
  ativa: ["Ativa", "bg-green-brand/10 text-green-300 border-green-brand/40"],
  atrasada: ["Atrasada", "bg-red-950/40 text-red-300 border-red-900"],
  cortesia: ["Cortesia", "bg-fuchsia-950/40 text-fuchsia-300 border-fuchsia-800"],
  bloqueada: ["Bloqueada", "bg-zinc-800 text-zinc-300 border-zinc-600"],
};

export function statusDetail(meta) {
  const b = meta?.billing || {};
  if (meta?.status === "teste" || meta?.status === "teste_vencido") return `${b.trial_start ? `de ${fmt(b.trial_start)} ` : ""}até ${fmt(b.trial_end)}`;
  if (meta?.status === "cortesia") return b.cortesia_until ? `até ${fmt(b.cortesia_until)}` : "indeterminada";
  if (meta?.status === "bloqueada") return b.suspended_at ? `desde ${fmt(b.suspended_at)}` : "";
  if (b.activated_at || b.start_date) return `desde ${fmt(b.activated_at || b.start_date)}`;
  return "";
}

export function StatusPill({ shop, meta }) {
  const key = !shop.is_active ? "bloqueada" : meta?.status || "ativa";
  const [label, cls] = STATUS[key] || STATUS.ativa;
  return (
    <div className="text-right shrink-0">
      <span data-testid={`shop-status-${shop.id}`} className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${cls}`}>
        {shop.is_active ? label : "Suspensa"}
      </span>
      <div data-testid={`shop-status-dates-${shop.id}`} className="text-[10px] text-zinc-500 mt-0.5">{statusDetail(meta)}</div>
      {meta?.login_locked && (
        <span data-testid={`shop-login-locked-${shop.id}`} className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border bg-red-950/40 text-red-300 border-red-900">
          <Lock className="h-3 w-3" /> Login bloqueado
        </span>
      )}
    </div>
  );
}

export function LastAccess({ shop, meta }) {
  const la = meta?.last_access;
  const days = la ? Math.floor((Date.now() - new Date(la).getTime()) / 86400000) : null;
  const red = days == null || days >= 7;
  return (
    <div data-testid={`shop-last-access-${shop.id}`} className={`text-[11px] mt-2 ${red ? "text-red-400 font-semibold" : "text-zinc-500"}`}>
      Último acesso: {la ? `${new Date(la).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })} (${days === 0 ? "hoje" : `há ${days} dia${days > 1 ? "s" : ""}`})` : "nunca acessou"}
    </div>
  );
}

const BTNS = [
  ["trial", "Liberar teste", Timer, "14 dias de teste a partir de hoje"],
  ["extend", "Estender +7", CalendarPlus, "Mais 7 dias de teste"],
  ["activate", "Ativar assinatura", BadgeCheck, "Marca como assinante"],
  ["cortesia", "Cortesia", Gift, "Sem pagamento"],
];

export function UnlockLoginButton({ shop, meta, onMeta, testid }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.post(`/admin/shops/${shop.id}/unlock-login`); onMeta?.(shop.id, { ...(meta || {}), login_locked: false }); toast.success("Login do dono desbloqueado"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <button type="button" data-testid={testid || `shop-unlock-login-${shop.id}`} onClick={go} disabled={busy}
            className={`w-full h-8 rounded-md border text-[11px] font-semibold inline-flex items-center justify-center gap-1 disabled:opacity-50 ${meta?.login_locked ? "border-red-800 bg-red-950/40 text-red-200 hover:bg-red-900/40" : "border-zinc-800 bg-zinc-900/60 text-zinc-300 hover:bg-zinc-800"}`}>
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LockOpen className="h-3.5 w-3.5" />} Desbloquear login
    </button>
  );
}

export function ShopAdminActions({ shop, meta, onMeta }) {
  const [busy, setBusy] = useState("");
  const act = async (action) => {
    let until = null;
    if (action === "cortesia") {
      const v = window.prompt("Cortesia até quando? (dd/mm/aaaa) — deixe vazio para indeterminado", "");
      if (v === null) return;
      const m = v.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (v.trim() && !m) { toast.error("Data inválida"); return; }
      until = m ? `${m[3]}-${m[2]}-${m[1]}` : null;
    }
    setBusy(action);
    try { const { data } = await api.post(`/admin/shops/${shop.id}/status`, { action, until }); onMeta(shop.id, data); toast.success("Status atualizado"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const enter = async () => {
    setBusy("imp");
    try { await api.post(`/auth/impersonate/${shop.id}`); window.location.href = "/owner"; }
    catch (e) { toast.error(formatApiError(e)); setBusy(""); }
  };
  return (
    <div className="mt-3 space-y-2">
      <button type="button" data-testid={`shop-impersonate-${shop.id}`} onClick={enter} disabled={!!busy}
              className="w-full h-9 rounded-md bg-zinc-100 text-zinc-900 text-xs font-bold uppercase inline-flex items-center justify-center gap-1.5 hover:bg-white disabled:opacity-50">
        {busy === "imp" ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />} Entrar como a oficina
      </button>
      <div className="grid grid-cols-2 gap-1.5">
        {BTNS.map(([a, label, Icon, title]) => (
          <button key={a} type="button" title={title} data-testid={`shop-action-${a}-${shop.id}`} onClick={() => act(a)} disabled={!!busy}
                  className="h-8 rounded-md border border-zinc-800 bg-zinc-900/60 text-zinc-300 text-[11px] font-semibold inline-flex items-center justify-center gap-1 hover:bg-zinc-800 disabled:opacity-50">
            {busy === a ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />} {label}
          </button>
        ))}
      </div>
      <UnlockLoginButton shop={shop} meta={meta} onMeta={onMeta} />
      <Link to={`/admin/oficinas/${shop.id}/restaurar`} data-testid={`shop-restore-${shop.id}`}
            className="w-full h-8 rounded-md border border-zinc-800 bg-zinc-900/60 text-zinc-300 text-[11px] font-semibold inline-flex items-center justify-center gap-1 hover:bg-zinc-800">
        <DatabaseBackup className="h-3.5 w-3.5" /> Restaurar oficina
      </Link>
    </div>
  );
}
